import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";

type PontoOperacionalTipo = "MECOM" | "B2B" | "BENEF" | "OUTROS";

const TIPOS: PontoOperacionalTipo[] = ["MECOM", "B2B", "BENEF", "OUTROS"];

export default function CadastroPO() {
  const { data: list, refetch, isLoading } = trpc.pontosOperacionais.list.useQuery();
  const { data: listComNome, refetch: refetchComNome, isLoading: isLoadingComNome } = trpc.pontosOperacionais.comNome.list.useQuery();
  const pontos = (listComNome ?? list) as any[] | undefined;

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [tipo, setTipo] = useState<PontoOperacionalTipo>("MECOM");
  const [nome, setNome] = useState("");

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const createMutation = trpc.pontosOperacionais.create.useMutation({
    onSuccess: () => {
      toast.success("Ponto Operacional criado com sucesso.");
      refetch();
      setModalOpen(false);
    },
    onError: (err) => toast.error("Erro ao criar Ponto Operacional: " + err.message),
  });

  const updateMutation = trpc.pontosOperacionais.update.useMutation({
    onSuccess: () => {
      toast.success("Ponto Operacional atualizado com sucesso.");
      refetch();
      setModalOpen(false);
    },
    onError: (err) => toast.error("Erro ao atualizar Ponto Operacional: " + err.message),
  });

  const createComNomeMutation = trpc.pontosOperacionais.comNome.create.useMutation({
    onSuccess: () => {
      toast.success("Ponto Operacional criado com sucesso.");
      refetch();
      refetchComNome();
      setModalOpen(false);
    },
    onError: (err) => toast.error("Erro ao criar Ponto Operacional: " + err.message),
  });

  const updateComNomeMutation = trpc.pontosOperacionais.comNome.update.useMutation({
    onSuccess: () => {
      toast.success("Ponto Operacional atualizado com sucesso.");
      refetch();
      refetchComNome();
      setModalOpen(false);
    },
    onError: (err) => toast.error("Erro ao atualizar Ponto Operacional: " + err.message),
  });

  const deleteMutation = trpc.pontosOperacionais.delete.useMutation({
    onSuccess: () => {
      toast.success("Ponto Operacional excluído com sucesso.");
      refetch();
      refetchComNome();
      setDeleteConfirmId(null);
    },
    onError: (err) => toast.error("Erro ao excluir Ponto Operacional: " + err.message),
  });

  function openNew() {
    setEditingId(null);
    setTipo("MECOM");
    setNome("");
    setModalOpen(true);
  }

  function openEdit(id: number, currentTipo: PontoOperacionalTipo, currentNome?: string | null) {
    setEditingId(id);
    setTipo(currentTipo);
    setNome(currentNome || "");
    setModalOpen(true);
  }

  function handleSave() {
    if (editingId !== null) {
      updateComNomeMutation.mutate({ id: editingId, tipo, nome: nome.trim() || null });
    } else {
      createComNomeMutation.mutate({ tipo, nome: nome.trim() || null });
    }
  }

  function formatDate(dateStr: string) {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cadastro de PO</h1>
          <p className="text-muted-foreground text-sm">Gerenciamento de Pontos Operacionais</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="w-4 h-4 mr-2" />
          Novo PO
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pontos Operacionais</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading || isLoadingComNome ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : !pontos || pontos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum Ponto Operacional cadastrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">ID</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Tipo</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Nome</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Data de Criação</th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pontos.map((po: any) => (
                    <tr key={po.id} className="border-b hover:bg-muted/40 transition-colors">
                      <td className="py-2 px-3">{po.id}</td>
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary">
                          {po.tipo}
                        </span>
                      </td>
                      <td className="py-2 px-3">{po.nome || "-"}</td>
                      <td className="py-2 px-3 text-muted-foreground">{formatDate(po.criadoEm)}</td>
                      <td className="py-2 px-3 text-right space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(po.id, po.tipo as PontoOperacionalTipo, po.nome)}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Editar
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDeleteConfirmId(po.id)}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Excluir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Novo / Editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingId !== null ? "Editar Ponto Operacional" : "Novo Ponto Operacional"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Nome</label>
              <Input value={nome} onChange={(event) => setNome(event.target.value)} placeholder="Nome da bancada" maxLength={100} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Tipo</label>
              <Select value={tipo} onValueChange={(v) => setTipo(v as PontoOperacionalTipo)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              disabled={createComNomeMutation.isPending || updateComNomeMutation.isPending}
            >
              {editingId !== null ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmação Exclusão */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Tem certeza que deseja excluir o Ponto Operacional <strong>#{deleteConfirmId}</strong>? Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId !== null && deleteMutation.mutate({ id: deleteConfirmId })}
              disabled={deleteMutation.isPending}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
